/**
 * ZAXIA Staff Protected Route Component
 * Route-level protection for authenticated-only content
 *
 * Phase P2.1-4: Protected Routes & Access Control
 *
 * This component ensures:
 * - Only authenticated users can access protected pages
 * - Loading state is shown while validating session
 * - Unauthenticated users are redirected to login
 * - No UI flicker or partial content exposure
 */

import { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { LoginPage } from '../pages';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Loading screen shown while auth is being validated
 * Minimal design to avoid UI flicker
 */
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen bg-zaxia-dark flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto mb-3 border-2 border-zaxia-teal border-t-transparent rounded-full animate-spin" />
        <p className="text-zaxia-gray text-sm">Checking session…</p>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute Component
 *
 * Wraps content that requires authentication.
 * Handles three states:
 * 1. isLoading = true → Show loading screen
 * 2. !isAuthenticated → Redirect to login
 * 3. isAuthenticated → Render children
 *
 * @example
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  // State 1: Auth is being validated
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  // State 2: Not authenticated - show login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // State 3: Authenticated - render protected content
  return <>{children}</>;
}
